import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { getEnrollments, isAuthenticated } from '@/lib/memberAuth';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import {
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
  TrendingUp,
  FileText,
  Target,
} from 'lucide-react';

interface Enrollment {
  id: number;
  program_name: string;
  program_description?: string;
  status: string;
  enrollment_date: string;
  completion_date?: string;
  discharge_date?: string;
  progress_percentage?: number;
  goals?: string;
  notes?: string;
}

export default function MemberEnrollmentsPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/member/login');
      return;
    }

    loadEnrollments();
  }, [navigate]);

  const loadEnrollments = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getEnrollments();
      setEnrollments(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string; icon: any }> = {
      active: {
        label: 'Active',
        className: 'bg-green-50 text-green-700 border-green-200',
        icon: CheckCircle,
      },
      enrolled: {
        label: 'Enrolled',
        className: 'bg-green-50 text-green-700 border-green-200',
        icon: CheckCircle,
      },
      pending: {
        label: 'Pending',
        className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        icon: Clock,
      },
      completed: {
        label: 'Completed',
        className: 'bg-blue-50 text-blue-700 border-blue-200',
        icon: CheckCircle,
      },
      withdrawn: {
        label: 'Withdrawn',
        className: 'bg-paper text-ink-700 border-v2border',
        icon: XCircle,
      },
      discharged: {
        label: 'Discharged',
        className: 'bg-purple-50 text-purple-700 border-purple-200',
        icon: CheckCircle,
      },
    };

    const config = statusConfig[status.toLowerCase()] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant="outline" className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDuration = (startDate: string, endDate?: string) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }

    const months = Math.floor(diffDays / 30);
    if (months < 12) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    }

    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    }
    return `${years} year${years !== 1 ? 's' : ''}, ${remainingMonths} month${
      remainingMonths !== 1 ? 's' : ''
    }`;
  };

  const activeEnrollments = enrollments.filter(
    (e) => e.status.toLowerCase() === 'active' || e.status.toLowerCase() === 'enrolled'
  );
  const completedEnrollments = enrollments.filter(
    (e) => e.status.toLowerCase() === 'completed' || e.status.toLowerCase() === 'discharged'
  );

  const pageHeader = (
    <PageHeader
      leading={
        <Link to="/member/dashboard">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('enrollments.back', 'Back')}
          </Button>
        </Link>
      }
      title={t('enrollments.my_programs', 'My Programs')}
      description={`${enrollments.length} enrollment${enrollments.length !== 1 ? 's' : ''} · ${activeEnrollments.length} active`}
    />
  );

  if (loading) {
    return (
      <>
        {pageHeader}
        <PageContainer size="wide">
          <PageState variant="loading" />
        </PageContainer>
      </>
    );
  }

  return (
    <>
      {pageHeader}
      <PageContainer size="wide">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {enrollments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-ink-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-ink mb-2">
                {t('enrollments.no_program_enrollments_yet', 'No program enrollments yet')}
              </h3>
              <p className="text-ink-500">
                You are not currently enrolled in any programs. Your case manager will enroll you
                when appropriate programs become available.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Active Enrollments */}
            {activeEnrollments.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-ink mb-4">{t('enrollments.active_programs', 'Active Programs')}</h2>
                <div className="space-y-6">
                  {activeEnrollments.map((enrollment) => (
                    <Card key={enrollment.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <CardTitle className="text-xl">
                                {enrollment.program_name}
                              </CardTitle>
                              {getStatusBadge(enrollment.status)}
                            </div>
                            {enrollment.program_description && (
                              <CardDescription className="text-base">
                                {enrollment.program_description}
                              </CardDescription>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Progress */}
                        {enrollment.progress_percentage !== undefined &&
                          enrollment.progress_percentage !== null && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <Label className="text-sm font-medium text-ink-700 flex items-center">
                                  <TrendingUp className="w-4 h-4 mr-1" />
                                  {t('enrollments.progress', 'Progress')}
                                </Label>
                                <span className="text-sm font-medium text-teal-600">
                                  {enrollment.progress_percentage}%
                                </span>
                              </div>
                              <Progress value={enrollment.progress_percentage} className="h-2" />
                            </div>
                          )}

                        {/* Goals */}
                        {enrollment.goals && (
                          <div>
                            <Label className="text-sm font-medium text-ink-700 flex items-center">
                              <Target className="w-4 h-4 mr-1" />
                              {t('enrollments.program_goals', 'Program Goals')}
                            </Label>
                            <p className="mt-1 text-ink whitespace-pre-wrap">
                              {enrollment.goals}
                            </p>
                          </div>
                        )}

                        {/* Notes */}
                        {enrollment.notes && (
                          <div>
                            <Label className="text-sm font-medium text-ink-700">{t('enrollments.notes', 'Notes')}</Label>
                            <p className="mt-1 text-ink whitespace-pre-wrap">
                              {enrollment.notes}
                            </p>
                          </div>
                        )}

                        {/* Dates */}
                        <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                          <div>
                            <Label className="text-sm font-medium text-ink-700 flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {t('enrollments.enrolled', 'Enrolled')}
                            </Label>
                            <p className="mt-1 text-ink">
                              {formatDate(enrollment.enrollment_date)}
                            </p>
                            <p className="text-xs text-ink-500 mt-1">
                              {getDuration(enrollment.enrollment_date)} ago
                            </p>
                          </div>
                          {enrollment.completion_date && (
                            <div>
                              <Label className="text-sm font-medium text-ink-700 flex items-center">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                {t('enrollments.expected_completion', 'Expected Completion')}
                              </Label>
                              <p className="mt-1 text-ink">
                                {formatDate(enrollment.completion_date)}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center space-x-3 pt-4">
                          <Link to={`/member/notes?enrollment_id=${enrollment.id}`}>
                            <Button variant="outline" size="sm">
                              <FileText className="w-4 h-4 mr-2" />
                              {t('enrollments.add_note', 'Add Note')}
                            </Button>
                          </Link>
                          <Link to={`/member/documents?enrollment_id=${enrollment.id}`}>
                            <Button variant="outline" size="sm">
                              <FileText className="w-4 h-4 mr-2" />
                              {t('enrollments.upload_document', 'Upload Document')}
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Completed Enrollments */}
            {completedEnrollments.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-ink mb-4">{t('enrollments.completed_programs', 'Completed Programs')}</h2>
                <div className="space-y-4">
                  {completedEnrollments.map((enrollment) => (
                    <Card key={enrollment.id} className="border-v2border bg-gray-50/50">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <CardTitle className="text-lg">
                                {enrollment.program_name}
                              </CardTitle>
                              {getStatusBadge(enrollment.status)}
                            </div>
                            {enrollment.program_description && (
                              <CardDescription>{enrollment.program_description}</CardDescription>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <Label className="text-sm font-medium text-ink-700">{t('enrollments.enrolled_1', 'Enrolled')}</Label>
                            <p className="text-ink">
                              {formatDate(enrollment.enrollment_date)}
                            </p>
                          </div>
                          {(enrollment.completion_date || enrollment.discharge_date) && (
                            <div>
                              <Label className="text-sm font-medium text-ink-700">
                                {enrollment.completion_date ? 'Completed' : 'Discharged'}
                              </Label>
                              <p className="text-ink">
                                {formatDate(
                                  enrollment.completion_date || enrollment.discharge_date || ''
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-ink-500">
                          Duration:{' '}
                          {getDuration(
                            enrollment.enrollment_date,
                            enrollment.completion_date || enrollment.discharge_date
                          )}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </PageContainer>
    </>
  );
}

function Label({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <label className={`block text-sm font-medium ${className}`}>{children}</label>;
}
